import Alpine from "alpinejs";

// Print-to-PDF size is driven by these pixel caps, not QUALITY; Safari embeds
// near-lossless and ignores QUALITY, so it needs much smaller caps.
const CAPS = {
    default: { hero: 1800, tile: 850 },
    safari:  { hero: 800,  tile: 300 },
}
const QUALITY = 0.8

// Match Safari (incl. iOS) without matching Chromium browsers, whose UA also says "Safari".
function isSafari(): boolean {
    const ua = navigator.userAgent
    return /safari/i.test(ua) && !/chrome|chromium|crios|fxios|edg|edgios|android|opr|opera/i.test(ua)
}

const PRINT_PHOTO_SELECTOR =
    ".OfferHeroPhoto a img, .OfferGalleryItem:not(.OfferGalleryItem--video) img"

function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve(img)
        img.onerror = reject
        img.src = src
    })
}

async function reencodeForPrint(): Promise<() => void> {
    const imgs = Array.from(document.querySelectorAll<HTMLImageElement>(PRINT_PHOTO_SELECTOR))
    const caps = isSafari() ? CAPS.safari : CAPS.default

    const originals = imgs.map((img) => ({
        img,
        src: img.getAttribute("src"),
        srcset: img.getAttribute("srcset"),
        sizes: img.getAttribute("sizes"),
        loading: img.getAttribute("loading"),
    }))

    await Promise.all(imgs.map(async (img) => {
        const href = img.closest("a")?.getAttribute("href")
        if (!href) return

        try {
            const source = await loadImage(href)
            const maxEdge = img.closest(".OfferHeroPhoto") ? caps.hero : caps.tile
            const scale = Math.min(1, maxEdge / Math.max(source.naturalWidth, source.naturalHeight))
            const w = Math.round(source.naturalWidth * scale)
            const h = Math.round(source.naturalHeight * scale)

            const canvas = document.createElement("canvas")
            canvas.width = w
            canvas.height = h
            canvas.getContext("2d")?.drawImage(source, 0, 0, w, h)

            img.removeAttribute("loading")
            img.removeAttribute("srcset")
            img.removeAttribute("sizes")
            img.src = canvas.toDataURL("image/jpeg", QUALITY)
            await img.decode().catch(() => {})
        } catch {}
    }))

    return () => originals.forEach(({ img, src, srcset, sizes, loading }) => {
        const set = (name: string, value: string | null) =>
            value === null ? img.removeAttribute(name) : img.setAttribute(name, value)
        set("src", src)
        set("srcset", srcset)
        set("sizes", sizes)
        set("loading", loading)
    })
}

export default function () {
    document.addEventListener("alpine:init", () => {
        Alpine.bind("AppPrintButton", {
            async "@click.prevent"() {
                document.body.classList.add("is-printing")

                const restore = await reencodeForPrint()

                if (isSafari()) {
                    document.querySelector("[data-gallery-swiper]")?.scrollIntoView()
                }

                let cleaned = false
                const done = () => {
                    if (cleaned) return
                    cleaned = true
                    restore()
                    window.scrollTo(0, 0)
                    document.body.classList.remove("is-printing")
                    window.removeEventListener("afterprint", done)
                }
                window.addEventListener("afterprint", done)

                setTimeout(() => window.print(), 200)
                setTimeout(done, 8000) // safety net if afterprint never fires
            },
        })
    })
}
